<script setup lang="ts">
import { computed, ref } from 'vue'

withDefaults(
    defineProps<{
        align?: 'start' | 'center' | 'end';
        placement?: 'top' | 'right' | 'bottom' | 'left';
        disabled?: boolean;
        fullWidth?: boolean;
        triggerAs?: 'span' | 'button';
        cursorTracking?: boolean
    }>(),
    {
        align: 'center',
        disabled: false,
        fullWidth: false,
        placement: 'top',
        triggerAs: 'span',
        cursorTracking: false,
    }
)

const open = ref(false)
const anchor = ref({ x: 0, y: 0 })

const virtualPosition = computed(() => ({
    getBoundingClientRect: () =>
        ({
            width: 0,
            height: 0,
            top: anchor.value.y,
            bottom: anchor.value.y,
            left: anchor.value.x,
            right: anchor.value.x,
            x: anchor.value.x,
            y: anchor.value.y,
            toJSON: () => {},
        } as DOMRect),
}))
</script>

<template>
    <ClientOnly>
        <TooltipProvider :delay-duration="0" :disabled>
            <TooltipRoot :open="cursorTracking ? open : undefined">
                <TooltipTrigger
                    v-if="cursorTracking && !disabled"
                    :as="triggerAs"
                    :class="[
                        {
                            'w-full': fullWidth,
                        },
                    ]"
                    @pointerenter="open = true"
                    @pointerleave="open = false"
                    @pointermove="(e) => {
                        anchor.x = e.clientX
                        anchor.y = e.clientY
                      }"
                >
                    <slot />
                </TooltipTrigger>

                <TooltipTrigger
                    v-else
                    :as="triggerAs"
                    :class="[
                        {
                            'w-full': fullWidth,
                        },
                    ]"
                >
                    <slot />
                </TooltipTrigger>
                <TooltipPortal>
                    <Transition
                        enter-active-class="transition-opacity duration-100 ease-[cubic-bezier(.75,.11,.85,.01)]"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition-opacity duration-300 ease-[cubic-bezier(.25,.89,.15,.99)]"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <TooltipContent
                            :side="placement"
                            :align
                            :side-offset="5"
                            :reference="cursorTracking ? virtualPosition : undefined"
                            :update-position-strategy="cursorTracking ? 'always' : undefined"
                            class="z-50 max-w-[270px] rounded bg-gray-700 px-4 py-3"
                        >
                            <BaseText color="text-white">
                                <slot name="info" />
                            </BaseText>
                            <TooltipArrow
                                class="fill-gray-700"
                                :width="15"
                                :height="8"
                            />
                        </TooltipContent>
                    </Transition>
                </TooltipPortal>
            </TooltipRoot>
        </TooltipProvider>
    </ClientOnly>
</template>
